// Presentation helpers over the backend-resolved HeaderBlock contract.
// Shared by every artifact list row surface (workspace tabs, Library,
// home feed) so the "one dumb renderer" principle holds — these encode
// the frontend-side stopgaps we carry until the stak header resolver
// contract grows the equivalent semantics (tag kinds, editorial
// eyebrows, clean descriptions). Each helper documents the backend
// change that deletes it.

import { ARTIFACT_REGISTRY } from './registry';
import { URGENCY_LABELS } from './types';

import type { HeaderTag } from './content-blocks';
import type { ArtifactType } from './types';

// The registry's chip label is curated Klue copy ("Trending Quotes",
// "News Digest"); the backend eyebrow is derived from the schema's
// display_name and leaks internal taxonomy ("Prospect Quotes Rollup
// Topics"). Prefer the registry so list surfaces read in product
// language, and strip the "Artifact" suffix some entries still carry.
// Stopgap until the resolver emits editorial eyebrows for every type.
// Suffix-matched rather than regex-replaced: an anchored `\s+` pattern
// trips the redos lint rule, and these are ordinary single-spaced labels.
const ARTIFACT_SUFFIXES = [' artifacts', ' artifact'];

export function resolveTypeChipLabel(
  artifactType: string,
  eyebrow?: string,
): string {
  const registryLabel =
    ARTIFACT_REGISTRY[artifactType as ArtifactType]?.typeChipLabel;
  const label = registryLabel ?? eyebrow ?? artifactType;
  const lower = label.toLowerCase();
  const suffix = ARTIFACT_SUFFIXES.find((candidate) =>
    lower.endsWith(candidate),
  );
  return suffix ? label.slice(0, -suffix.length) : label;
}

// Tile-label audit R2: preview surfaces carry one chip by default, two
// at most — detail views may carry more. The backend registry already
// caps registered types (`max_tags`), and the schema-declared chip work
// (R1, backend) will enforce this at the resolver; this constant is the
// frontend-side guarantee in the meantime, so no misconfigured type can
// stack badges on a tile. Applied by the preview renderers only.
export const PREVIEW_MAX_HEADER_TAGS = 2;

const URGENCY_TAG_LABELS = new Set<string>(
  Object.values(URGENCY_LABELS).map((label) => label.toLowerCase()),
);

export type SplitHeaderTags = {
  // Severity tags ("Medium" / "High" / "Critical") — list rows show these
  // next to the type label, where urgency reads as part of the kicker.
  urgencyTags: HeaderTag[];
  // Everything else — outcome/status tags (win-loss "Won"/"Lost", deal
  // stage) that belong with the row's metadata rail.
  statusTags: HeaderTag[];
};

// Label-based classification is a stopgap: HeaderTag carries only
// {label, tone}, so urgency is recognized by its known label set.
// Deleted the day stak adds `HeaderTag.kind`.
export function splitHeaderTags(
  tags: HeaderTag[] | undefined,
): SplitHeaderTags {
  const urgencyTags: HeaderTag[] = [];
  const statusTags: HeaderTag[] = [];
  for (const tag of tags ?? []) {
    if (URGENCY_TAG_LABELS.has(tag.label.toLowerCase())) {
      urgencyTags.push(tag);
    } else {
      statusTags.push(tag);
    }
  }
  return { urgencyTags, statusTags };
}

// Some resolver descriptions leak markdown emphasis markers (e.g. the
// hiring_signal_block "**7 open roles**"). The preview renderer prints
// plain text, so strip the markers rather than showing them literally.
// Deliberately narrow — only the observed leak patterns; anything
// smarter belongs in the resolver (the real fix is markdown-free
// descriptions at the contract level).
export function stripInlineMarkdown(text: string): string {
  return text.replaceAll('**', '').replaceAll('`', '');
}
